import { GaiaWebSocketEvent } from "../types";

/**
 * Inbound events that are inherently not comparable between the live GAIA socket and the Simplr
 * shadow socket. The comparator never pairs them (from either side), so they produce no per-message
 * metric, log, or analytic; it hands them to `onUncompared` instead, which counts them per side in the
 * tally summary. Each socket still handles them normally.
 *
 * - `call-transcript`: both backends push transcript frames, but GAIA mints a fresh correlationId per
 *   frame and Simplr does not, so they can never pair. By far the highest-volume source of `*-only`
 *   noise; per-side frame and snippet counts are the useful signal.
 * - `heartbeat-success` / `heartbeat-error`: replies to each socket's own keepalive ping
 *   (react-use-websocket heartbeat), a per-connection concern rather than comparable server
 *   behavior. Not in `GaiaWebSocketEvent` because the app never consumes them.
 * - `authorization-success` / `unauthorized-connection`: each socket's own auth handshake. The two
 *   sockets (re)connect independently, so these rarely land inside one match window. Read their
 *   counts as the Simplr handshake signal: GAIA messages only reach the comparator while the shadow
 *   is connected, so GAIA's own auth (which happens first) is usually not counted.
 */
const NON_COMPARABLE_EVENTS: ReadonlySet<string> = new Set<string>([
    GaiaWebSocketEvent.TranscriptSnippets,
    "heartbeat-success",
    "heartbeat-error",
    GaiaWebSocketEvent.AuthorizationSuccess,
    GaiaWebSocketEvent.UnauthorizedConnection,
]);

export function isShadowComparable(name: string): boolean {
    return !NON_COMPARABLE_EVENTS.has(name);
}
